
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export const useUserRole = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.log('[useUserRole] No user ID, returning null');
        return null;
      }
      
      console.log('[useUserRole] Fetching role for user:', user.id);
      
      // Try direct table query first
      const { data: tableData, error: tableError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!tableError && tableData?.role) {
        console.log('[useUserRole] ✅ Table query succeeded:', tableData.role);
        return tableData.role;
      }
      
      console.warn('[useUserRole] ⚠️ Table query failed, trying RPC fallback:', tableError);
      
      // Fallback to RPC function
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_user_role_safe');
      
      if (rpcError) {
        console.error('[useUserRole] ❌ RPC fallback also failed:', rpcError);
        return 'user';
      }
      
      console.log('[useUserRole] ✅ RPC fallback succeeded:', rpcData);
      return rpcData || 'user';
    },
    enabled: !!user?.id,
    staleTime: 0, // Force fresh fetch every time (temporary for debugging)
    gcTime: 0, // Don't cache at all (temporary for debugging)
  });
};

export const useIsAdmin = () => {
  const { data: role, isLoading } = useUserRole();
  return {
    isAdmin: role === 'admin' || role === 'site_admin',
    isSiteAdmin: role === 'site_admin',
    isOrgAdmin: role === 'org_admin',
    hasAdminAccess: ['admin', 'site_admin', 'org_admin', 'test_user'].includes(role || ''),
    isLoading,
  };
};
